import { useProductPriceProps } from '@bluheadless/ui/src/molecules/product-price'
import axios from 'axios'
import useSWR from 'swr'
import {
	PRICE_MODE_FROM_TO,
	PRICE_MODE_STARTING_FROM,
	PRODUCT_BUNDLE_PRICE_TYPE_DYNAMIC,
	PRODUCT_TYPE_BUNDLE,
	PRODUCT_TYPE_CONFIGURABLE,
	PRODUCT_TYPE_SIMPLE,
	PRODUCT_TYPE_VIRTUAL,
} from '../../constants'
import { useConfig } from '../../providers/config'
import { useProductContext } from '../../providers/product'
import { formatResponseError } from '../../utils'

/**
 * Can show omnibus price if product type is one of the following:
 * - simple
 * - configurable
 * - bundle (only fixed price)
 * - virtual
 */
const canShowOmnibusPrice = (type, bundleOptions) => {
	return type === PRODUCT_TYPE_BUNDLE && bundleOptions?.priceType !== PRODUCT_BUNDLE_PRICE_TYPE_DYNAMIC
		? true
		: [PRODUCT_TYPE_SIMPLE, PRODUCT_TYPE_CONFIGURABLE, PRODUCT_TYPE_VIRTUAL].includes(type)
}

/**
 * @description This hook is used to get the lowest price of the product of the last 30 days
 * @returns {number} - The lowest price of the product of the last 30 days or the current price
 */
export const useOmnibusDirective = () => {
	const {
		omnibus: { enabled, apiUrl, brand },
		groupName,
	} = useConfig()
	const [
		{
			product: { sku, type, bundleOptions } = {},
			prices: { base: defaultPrice } = {},
			selectedProduct: { sku: selectedSku } = {},
		},
	] = useProductContext()

	const { mode: priceMode, amount, specialAmount } = useProductPriceProps({ displayContext: 'default' })

	/**
	 * do not fetch omnibus price if:
	 * - omnibus is disabled
	 * - the product is not simple, configurable, bundle with fixed price or virtual
	 * - the product isn't discounted
	 * - the price mode is 'starting-from' and the selected sku is not defined
	 */
	const isPaused =
		!enabled ||
		(enabled &&
			(((priceMode === PRICE_MODE_STARTING_FROM || priceMode === PRICE_MODE_FROM_TO) && !selectedSku) ||
				amount === specialAmount ||
				!canShowOmnibusPrice(type, bundleOptions)))

	const { data, error, isValidating } = useSWR(
		`${apiUrl}/?brand=${brand}&sku=${selectedSku ?? sku}&market=${groupName}`,
		(url) =>
			axios({ method: 'GET', url })
				.then((res) => res.data)
				.catch((err) => {
					const error = formatResponseError(err)
					error.__CANCEL__ = err.__CANCEL__
					throw error
				}),
		{
			bhLocalPaused: isPaused,
		}
	)

	return {
		...data,
		...(!isPaused && !isValidating ? { price: data?.price ?? defaultPrice } : {}),
		error,
		loading: isValidating,
	}
}
